import React, { useMemo, useRef, useState } from 'react'
import randomColor from 'randomcolor'
import useSecurity from '../useSecurity/useSecurity'

import Icon from '../../Icon/Icon'
import Row from '../../Row/Row'
import Column from '../../Column/Column'
import Tooltip from '../../Tooltip/Tooltip'

export enum SecurityAccessEnum {
    READ,
    WRITE,
    DELETE,
    MANAGE,
}

export interface CanPerformProps {
    actionId: string
    withMessage?: boolean
    unauthorizedDisplay?: any
}

type SecuredContextType = {
    hasAccess: boolean
    unauthorizedMessageShort: string | React.ReactElement
    unauthorizedMessage: string | React.ReactElement
}

const UnauthorizedShortMessage = ({ actionId }) => {
    const noAccessRef = useRef()
    const [showTooltip, setShowTooltip] = useState(false)

    return (
        <span
            style={{ display: 'flex', alignItems: 'center' }}
            className={'r-color-red-l2'}
            ref={noAccessRef}
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
        >
            <Icon type={'ban'} />
            <Tooltip show={showTooltip} anchor={noAccessRef}>
                If you believe you are seeing this in error, please contact your administrator. <br />
                {actionId}
            </Tooltip>
            <span style={{ marginLeft: '5px' }}>No Access</span>
        </span>
    )
}

const CanPerform: React.FC<CanPerformProps> = ({ withMessage = false, actionId, children, unauthorizedDisplay }) => {
    const { isFetchingSecurity, canPerform, actions } = useSecurity()
    const hasAccess = useMemo(() => {
        return canPerform(actionId)
    }, [actionId, actions])

    if (isFetchingSecurity) {
        return null
    }

    const ctx: SecuredContextType = {
        hasAccess,
        unauthorizedMessageShort: <UnauthorizedShortMessage actionId={actionId} />,
        unauthorizedMessage: `It looks like you don't have access to this. (${actionId})`,
    }

    if (typeof children === 'function') {
        return children(ctx)
    }

    if (!hasAccess && withMessage && !isFetchingSecurity) {
        return (
            <div
                style={{
                    width: '100%',
                    height: '100%',
                }}
            >
                <Row center middleContent>
                    <Column>
                        Looks like you are trying to access a resource you don't have permission for. Please contact
                        your administrator if you believe this is an error.
                        <h6>{actionId}</h6>
                    </Column>
                </Row>
            </div>
        )
    } else if (!hasAccess) {
        return unauthorizedDisplay || null
    }

    return children
}

export default CanPerform
